import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share } from "lucide-react";

// Detect environment
function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
function isAndroid() {
  return /android/i.test(navigator.userAgent);
}
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

const DISMISSED_KEY = 'trackin_install_dismissed';
const DISMISS_TTL   = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function InstallPrompt() {
  const [show,        setShow]        = useState(false);
  const [mode,        setMode]        = useState(null); // 'ios' | 'android' | 'desktop' | 'update'
  const [deferredEvt, setDeferredEvt] = useState(null);
  const [updateReg,   setUpdateReg]   = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Already installed — nothing to prompt
    if (isStandalone()) return;

    // Check snooze
    const ts = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
    if (Date.now() - ts < DISMISS_TTL) return;

    // Listen for SW update event (fired from index.js)
    function handleSWUpdate(e) {
      setUpdateReg(e.detail);
      setMode('update');
      setShow(true);
    }
    window.addEventListener('sw:update', handleSWUpdate);

    // Listen for native install prompt (Chrome / Edge / Samsung Internet)
    function handleBeforeInstall(e) {
      e.preventDefault();
      setDeferredEvt(e);
      setMode(isAndroid() ? 'android' : 'desktop');
      timerRef.current = setTimeout(() => setShow(true), 20000); // show after 20s
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS manual instructions — show after 30s
    if (isIOS()) {
      timerRef.current = setTimeout(() => {
        setMode('ios');
        setShow(true);
      }, 30000);
    }

    return () => {
      window.removeEventListener('sw:update', handleSWUpdate);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(timerRef.current);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setShow(false);
  }

  async function handleInstall() {
    if (deferredEvt) {
      deferredEvt.prompt();
      const { outcome } = await deferredEvt.userChoice;
      if (outcome === 'accepted') setShow(false);
      else dismiss();
      setDeferredEvt(null);
    }
  }

  function handleUpdate() {
    if (!updateReg?.waiting) return;
    updateReg.waiting.postMessage({ type: 'SKIP_WAITING' });
    window.location.reload();
  }

  if (!show || !mode) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="install-prompt"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0,   opacity: 1 }}
          exit={{    y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 22, stiffness: 260 }}
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 32px)',
            maxWidth: 420,
            zIndex: 9999,
            background: '#1E293B',
            border: '1px solid #334155',
            borderRadius: 16,
            padding: '16px 18px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L14 5.5V10.5L8 14L2 10.5V5.5L8 2Z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
                <circle cx="8" cy="8" r="2" fill="#fff"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#F1F5F9' }}>
                {mode === 'update' ? 'Update Available' : 'Add LorryKhojo to Home Screen'}
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                {mode === 'update'
                  ? 'A new version of LorryKhojo is ready. Refresh to update.'
                  : 'Install the app for quick access and offline vehicle data.'}
              </div>
            </div>
            <button
              onClick={dismiss}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#64748B', padding: 4, display: 'flex',
              }}
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>

          {/* iOS instructions */}
          {mode === 'ios' && (
            <div style={{
              background: '#0F172A', borderRadius: 10, padding: '10px 14px',
              fontSize: 12, color: '#CBD5E1', lineHeight: 1.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Share size={13} color="#60A5FA" />
                <span>Tap <strong style={{ color: '#93C5FD' }}>Share</strong> in Safari</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Download size={13} color="#60A5FA" />
                <span>Then tap <strong style={{ color: '#93C5FD' }}>Add to Home Screen</strong></span>
              </div>
            </div>
          )}

          {/* Android/Desktop: native prompt */}
          {(mode === 'android' || mode === 'desktop') && (
            <button
              onClick={handleInstall}
              style={{
                width: '100%', height: 40, borderRadius: 9,
                background: '#4F46E5', border: 'none',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              <Download size={14} />
              Install App
            </button>
          )}

          {/* SW update */}
          {mode === 'update' && (
            <button
              onClick={handleUpdate}
              style={{
                width: '100%', height: 40, borderRadius: 9,
                background: '#16A34A', border: 'none',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Refresh to Update
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
